"use client";

import { useState } from "react";

import { LogicalFilter, useDataProvider, useParsed } from "@refinedev/core";
import { UseTableReturnType } from "@refinedev/react-table";
import type { Column } from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  EyeOff,
  Pin,
  PinOff,
  X,
} from "lucide-react";

import { DataTableDateFilter } from "@/components/refine-ui/data-table/data-table-date-filter";
import {
  DataTableFacetedFilter,
  IOption,
} from "@/components/refine-ui/data-table/data-table-faceted-filter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

type NumberOperator = "eq" | "gt" | "lt" | "gte" | "lte";

const NUMBER_OPERATORS: NumberOperator[] = ["eq", "gt", "lt", "gte", "lte"];

const NUMBER_OPERATOR_OPTIONS: { value: NumberOperator; label: string }[] = [
  { value: "eq", label: "=" },
  { value: "gt", label: ">" },
  { value: "lt", label: "<" },
  { value: "gte", label: "≥" },
  { value: "lte", label: "≤" },
];

interface DataTableColumnHeaderProps<TData, TValue> {
  column: Column<TData, TValue>;
  title: string;
  table: UseTableReturnType<any>;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  table,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const [isOpen, setIsOpen] = useState(false);
  const [filterValue, setFilterValue] = useState(
    (column.getFilterValue() as string) ?? "",
  );
  const [operator, setOperator] = useState("contains");
  const numberFilter = table.refineCore.filters.find(
    (f) =>
      (f as LogicalFilter).field === column.id &&
      NUMBER_OPERATORS.includes(f.operator as NumberOperator),
  );
  const [numberOperator, setNumberOperator] = useState<NumberOperator>(
    (numberFilter?.operator as NumberOperator) ?? "gte",
  );
  const [numberValue, setNumberValue] = useState<string>(
    numberFilter?.value != null ? String(numberFilter.value) : "",
  );
  const [facetData, setFacetData] = useState<any>(
    table.refineCore.tableQuery.data,
  );
  const { getList } = useDataProvider()();
  const parsed = useParsed();

  const isSorted = column.getIsSorted();
  const isPinned = column.getIsPinned();
  const isFiltered = !!column.getFilterValue();

  const isSortable = column.getCanSort();
  const isPinnable = column.getCanPin();
  const isFilterable = column.getCanFilter();

  const handleSort = (direction: "asc" | "desc" | false) => {
    column.toggleSorting(direction === "desc");
  };

  const handlePin = (position: "left" | "right" | false) => {
    column.pin(position);
  };

  const handleApplyFilter = () => {
    column.setFilterValue(filterValue);
    column.columnDef.meta = {
      ...column.columnDef.meta,
      filterOperator: operator,
    };
    setIsOpen(false);
  };

  const handleClearFilter = () => {
    setFilterValue("");
    setNumberValue("");
    column.setFilterValue(undefined);
    table.refineCore.setFilters((prev) =>
      prev.filter((f) => (f as LogicalFilter).field !== column.id),
    );
  };

  const filterComponent = (() => {
    const meta = column.columnDef.meta as
      | {
          filterType?: "text" | "select" | "number" | "date" | "faceted-select";
          options?: IOption[];
          optionAccessorFn?: (item: any) => IOption;
        }
      | undefined;

    const type = meta?.filterType ?? "text";

    const defaultValue = table.refineCore.filters.find(
      (f) => (f as LogicalFilter).field === column.id && f.operator === "eq",
    )?.value;
    switch (type) {
      case "select":
        return (
          <DataTableFacetedFilter
            defaultValues={[defaultValue]}
            column={column}
            title={column.columnDef.header as string}
            options={meta?.options ?? []}
            onFilterChanged={(value) => {
              if (value) {
                table.refineCore.setFilters(
                  [
                    {
                      field: column.id,
                      operator: "eq",
                      value: value,
                    },
                  ],
                  "merge",
                );
              }
            }}
            shouldFilter
            showClearFilters={false}
          />
        );

      case "faceted-select":
        return (
          <DataTableFacetedFilter
            data={facetData ?? table.refineCore.tableQuery.data}
            column={column}
            title={column.columnDef.header as string}
            defaultValues={[defaultValue]}
            optionAccessorFn={meta?.optionAccessorFn}
            searchFacet={async (query, columnId) => {
              const response = await getList({
                resource: parsed.resource!.name,
                pagination: {
                  pageSize: 1,
                  currentPage: 0,
                },
                filters: [],
                sorters: table.refineCore.sorters,
                meta: {
                  facetQueryBy: columnId,
                  facetQuery: query,
                  facetBy: [columnId],
                  includeFields: ["id"],
                  facetReturnParent: [columnId],
                },
              });
              setFacetData(response.data);
            }}
            onFilterChanged={(value) => {
              if (value) {
                table.refineCore.setFilters(
                  [
                    {
                      field: column.id,
                      operator: "eq",
                      value: value,
                    },
                  ],
                  "merge",
                );
                column.setFilterValue(value);
              } else {
                table.refineCore.setFilters((prev) => [
                  ...prev.filter(
                    (p) => (p as LogicalFilter).field !== column.id,
                  ),
                ]);
                // clear query params
                setFacetData(table.refineCore.tableQuery.data);
              }
            }}
            showClearFilters
          />
        );

      case "number": {
        const applyNumber = () => {
          const trimmed = numberValue.trim();
          if (trimmed === "" || Number.isNaN(Number(trimmed))) {
            column.setFilterValue(undefined);
            table.refineCore.setFilters((prev) =>
              prev.filter((f) => (f as LogicalFilter).field !== column.id),
            );
            setIsOpen(false);
            return;
          }
          const value = Number(trimmed);
          // Write the refine filter directly and leave TanStack columnFilters
          // untouched. Calling column.setFilterValue here would trip refine's
          // columnFilters→filters bridge, which re-emits the value with the
          // default "eq" operator and clobbers the chosen range operator.
          table.refineCore.setFilters((prev) => [
            ...prev.filter((f) => (f as LogicalFilter).field !== column.id),
            { field: column.id, operator: numberOperator, value },
          ]);
          setIsOpen(false);
        };
        return (
          <>
            <div className="flex flex-row items-center gap-1.5">
              <Select
                value={numberOperator}
                onValueChange={(v) => setNumberOperator(v as NumberOperator)}
              >
                <SelectTrigger className="h-8 w-20 text-sm font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NUMBER_OPERATOR_OPTIONS.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      className="font-mono"
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="number"
                step="any"
                placeholder="Value"
                value={numberValue}
                onChange={(e) => setNumberValue(e.target.value)}
                className="h-8 text-sm"
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyNumber();
                }}
              />
            </div>
            <Button size="sm" className="w-full h-8" onClick={applyNumber}>
              Apply
            </Button>
          </>
        );
      }

      case "date":
        return <DataTableDateFilter column={column} table={table} />;

      case "text":
      default:
        return (
          <>
            <Select value={operator} onValueChange={setOperator}>
              <SelectTrigger>
                <SelectValue placeholder="Select operator..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="eq">=</SelectItem>
                <SelectItem value="contains">contains</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex flex-row items-center">
              <Input
                id="filter"
                placeholder={`Filter ${title.toLowerCase()}...`}
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                className="h-8 text-sm"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleApplyFilter();
                  }
                }}
              />
            </div>
            <Button
              size="sm"
              className="w-full h-8"
              onClick={handleApplyFilter}
            >
              Apply
            </Button>
          </>
        );
    }
  })();

  // Decorative columns (no sort, filter, or pin) get a plain title — rendering
  // the interactive button + sort chevron would just clip in a narrow column
  // and imply controls that don't exist (e.g. the artwork thumbnail column).
  if (!isSortable && !isFilterable && !isPinnable) {
    return <span className="px-2 text-xs font-medium">{title}</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              " font-medium text-xs hover:bg-accent relative rounded-md",
              isFiltered && "text-primary",
            )}
          >
            <span className="text-balance">{title}</span>

            {/* Active filter indicator */}
            {isFiltered && (
              <Badge
                variant="secondary"
                className="ml-2 h-4 w-4 rounded-full p-0 flex items-center justify-center bg-primary/20 text-primary border-0"
              >
                <span className="sr-only">Filtered</span>
                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
              </Badge>
            )}

            {/* Sort indicator */}
            {isSorted === "desc" && (
              <ChevronDown className="ml-2 h-3.5 w-3.5 text-muted-foreground" />
            )}
            {isSorted === "asc" && (
              <ChevronUp className="ml-2 h-3.5 w-3.5 text-muted-foreground" />
            )}
            {!isSorted && (
              <ChevronsUpDown className="ml-2 h-3.5 w-3.5 text-muted-foreground opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-72 p-3 bg-backround/20 backdrop-blur-3xl border-foreground/20"
          align="start"
          sideOffset={8}
        >
          <div className="space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-foreground">{title}</h4>
              {isPinned && (
                <Badge variant="outline" className="h-5 text-[10px] px-1.5">
                  Pinned
                </Badge>
              )}
            </div>

            <Separator className="bg-border/50" />

            {/* Sort Section */}
            {isSortable && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                    Sort
                  </Label>
                  <div className="grid grid-cols-3 gap-1.5">
                    <Button
                      variant={isSorted === "asc" ? "default" : "outline"}
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => handleSort("asc")}
                    >
                      <ArrowUp className="mr-1.5 h-3 w-3" />
                      Asc
                    </Button>
                    <Button
                      variant={isSorted === "desc" ? "default" : "outline"}
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => handleSort("desc")}
                    >
                      <ArrowDown className="mr-1.5 h-3 w-3" />
                      Desc
                    </Button>
                    <Button
                      variant={!isSorted ? "default" : "outline"}
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => column.clearSorting()}
                      disabled={!isSorted}
                    >
                      <X className="mr-1.5 h-3 w-3" />
                      Clear
                    </Button>
                  </div>
                </div>

                <Separator className="bg-border/50" />
              </>
            )}

            {/* Filter Section */}
            {isFilterable && (
              <>
                <div className="space-y-2">
                  <Label
                    htmlFor="filter"
                    className="text-xs text-muted-foreground uppercase tracking-wide"
                  >
                    Filter
                  </Label>
                  <div className="space-y-2">
                    {filterComponent}
                    <div className="flex gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full h-8 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 bg-transparent"
                        onClick={handleClearFilter}
                        // disabled={!filterValue && !isFiltered}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                </div>

                <Separator className="bg-border/50" />
              </>
            )}

            {/* Pin Section */}
            {isPinnable && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                    Pin
                  </Label>
                  <div className="grid grid-cols-3 gap-1.5">
                    <Button
                      variant={isPinned === "left" ? "default" : "outline"}
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() =>
                        handlePin(isPinned === "left" ? false : "left")
                      }
                    >
                      <Pin className="mr-1.5 h-3 w-3 -rotate-45" />
                      Left
                    </Button>
                    <Button
                      variant={isPinned === "right" ? "default" : "outline"}
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() =>
                        handlePin(isPinned === "right" ? false : "right")
                      }
                    >
                      <Pin className="mr-1.5 h-3 w-3 rotate-45" />
                      Right
                    </Button>
                    <Button
                      variant={!isPinned ? "default" : "outline"}
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => handlePin(false)}
                      disabled={!isPinned}
                    >
                      <PinOff className="mr-1.5 h-3 w-3" />
                      None
                    </Button>
                  </div>
                </div>

                <Separator className="bg-border/50" />
              </>
            )}

            {/* Hide Section */}
            <Button
              variant="outline"
              size="sm"
              className="w-full h-8 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 bg-transparent"
              onClick={() => column.toggleVisibility(false)}
            >
              <EyeOff className="mr-2 h-3 w-3" />
              Hide Column
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
